import {
  Component, Input, signal, ElementRef, ViewChild,
  AfterViewInit, OnDestroy, PLATFORM_ID, inject
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-before-after',
  standalone: true,
  template: `
    <div #container
         class="relative w-full overflow-hidden select-none rounded-2xl"
         [style.aspect-ratio]="aspectRatio"
         (mousedown)="onDragStart($event)"
         (touchstart)="onDragStart($event)">
      <!-- After image (full, background) -->
      <div class="absolute inset-0 bg-surface-200">
        <img [src]="afterImage" [alt]="afterLabel" class="w-full h-full object-cover" draggable="false" />
        <span class="absolute bottom-4 right-4 px-3 py-1.5 rounded-lg bg-black/60 text-white text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">{{ afterLabel }}</span>
      </div>

      <!-- Before image (clipped) -->
      <div class="absolute inset-0 overflow-hidden"
           [style.width.%]="position()">
        <img [src]="beforeImage" [alt]="beforeLabel"
             class="absolute inset-0 w-full h-full object-cover"
             [style.width]="containerWidth ? containerWidth + 'px' : '100vw'"
             draggable="false" />
        <div class="absolute inset-0 bg-surface-900/30 pointer-events-none"
             [style.width]="containerWidth ? containerWidth + 'px' : '100vw'"></div>
        <span class="absolute bottom-4 left-4 px-3 py-1.5 rounded-lg bg-black/60 text-white text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">{{ beforeLabel }}</span>
      </div>

      <!-- Divider -->
      <div class="absolute top-0 bottom-0 z-10 -translate-x-1/2 pointer-events-none"
           [style.left.%]="position()">
        <div class="w-[2px] h-full bg-white shadow-lg"></div>
        <!-- Handle -->
        <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-xl flex items-center justify-center pointer-events-auto cursor-ew-resize border-2 border-white/80">
          <svg class="w-5 h-5 text-surface-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8 9l-3 3 3 3m8-6l3 3-3 3" />
          </svg>
        </div>
      </div>
    </div>
  `,
})
export class BeforeAfterComponent implements AfterViewInit, OnDestroy {
  @ViewChild('container') containerRef!: ElementRef<HTMLDivElement>;
  @Input() beforeImage = '';
  @Input() afterImage = '';
  @Input() beforeLabel = 'Before';
  @Input() afterLabel = 'After';
  @Input() aspectRatio = '16/10';

  position = signal(50);
  containerWidth = 0;
  private dragging = false;
  private platformId = inject(PLATFORM_ID);

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.containerWidth = this.containerRef.nativeElement.offsetWidth;

    window.addEventListener('mousemove', this.onDragMove);
    window.addEventListener('mouseup', this.onDragEnd);
    window.addEventListener('touchmove', this.onDragMove, { passive: false });
    window.addEventListener('touchend', this.onDragEnd);
    window.addEventListener('resize', this.onResize);
  }

  ngOnDestroy(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    window.removeEventListener('mousemove', this.onDragMove);
    window.removeEventListener('mouseup', this.onDragEnd);
    window.removeEventListener('touchmove', this.onDragMove);
    window.removeEventListener('touchend', this.onDragEnd);
    window.removeEventListener('resize', this.onResize);
  }

  onDragStart(e: MouseEvent | TouchEvent): void {
    this.dragging = true;
    this.updatePosition(e);
  }

  private onDragMove = (e: MouseEvent | TouchEvent): void => {
    if (!this.dragging) return;
    e.preventDefault();
    this.updatePosition(e);
  };

  private onDragEnd = (): void => {
    this.dragging = false;
  };

  private onResize = (): void => {
    this.containerWidth = this.containerRef.nativeElement.offsetWidth;
  };

  private updatePosition(e: MouseEvent | TouchEvent): void {
    const container = this.containerRef.nativeElement;
    const rect = container.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const pct = Math.max(2, Math.min(98, (x / rect.width) * 100));
    this.position.set(pct);
  }
}
